/**
 * wsSession.ts — WebSocket in potek seje (T2689)
 * ==============================================
 *
 * WebSocket se avtenticira enkrat, ob rokovanju, s piškotkom `access_token`
 * (`NotificationConsumer`/`ProjectConsumer`, T258). Dostopni žeton živi 15 min
 * in ga HTTP prestreznik (`services/api.ts`) osvežuje sam — WS za to ne ve.
 *
 * Izmerjeno 14.10.2026 na lokalnem zaledju:
 *
 *  - kanal obvestil ob izteku žetona povezavo **po** `accept()` zapre s
 *    **4001** (T382). Koda torej do brskalnika pride — za razliko od zavrnitev
 *    pred `accept()`, ki jih brskalnik vidi kot 1006 — in pomeni »žeton je
 *    potekel«, ne trajne zavrnitve. Odjemalec jo je od T1255 štel za
 *    terminalno, zato so obvestila v živo in anketiranje utihnila po prvem
 *    izteku žetona;
 *  - projektni kanal je ob izteku ostal odprt; ko je padel (omrežje, deploy),
 *    ga je rokovanje s potečenim piškotkom zavrnilo in odjemalec je po šestih
 *    poskusih odnehal (T1630). Ponovna prijava v oknu (T2376) ga ni obudila.
 *    Od T2703 ga zaledje ob izteku zapre s 4001 po istem mehanizmu kot kanal
 *    obvestil (`apps/core/ws_token_expiry.py`), zato velja zanj ista pot.
 *
 * Zato: 4001 sproži obnovo seje prek HTTP — en klic `auth/me/`, ki gre skozi
 * prestreznik in torej žeton tiho osveži ali, ko je seja res potekla, odpre
 * okno za ponovno prijavo in počaka nanjo — nato se kanal poveže znova.
 * Uspešna ponovna prijava v oknu (`reauthStore.renewals`) obudi vse kanale.
 */
import api from '../services/api';
import { useReauthStore } from '../store/reauthStore';
import { WS_CLOSE_AUTH_FAILED } from './orgBlock';

/** Ali je kanal zaprl strežnik, ker je dostopni žeton potekel? */
export function isWsSessionExpired(event: { code?: number } | undefined): boolean {
  return event?.code === WS_CLOSE_AUTH_FAILED;
}

/**
 * Obnovi sejo prek HTTP. Izpolni se, ko je piškotek spet veljaven (tiha
 * osvežitev ali ponovna prijava v oknu); zavrne se ob odjavi ali preklicu.
 */
export async function renewWsSession(): Promise<void> {
  await api.get('auth/me/');
}

/** Naroči se na uspešne ponovne prijave v oknu. Vrne funkcijo za odjavo. */
export function onSessionRenewed(callback: () => void): () => void {
  return useReauthStore.subscribe((state, prev) => {
    if (state.renewals !== prev.renewals) callback();
  });
}
